import React from 'react'
import { PGA_COM_HOST } from 'env'
import { get } from 'lodash'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined'
import CoachProfileCard from 'components/coach-profile-card'
import EmptyPage from 'components/empty-page-state'
import { PageHeader } from 'components/page-header'
import Error from 'components/error'
import { MY_COACH_PROFILE, ToggleCoachStatus } from './query'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { gtmEvent } from 'lib/gtm'
import QRCode from 'react-qr-code'
import CoachProfileAnalyticsCard from 'components/coach-profile-analytics-card'
import Hidden from '@material-ui/core/Hidden'
import { useStyles } from './styles'

const CoachProfileDisplay = () => {
  const history = useHistory()
  const classes = useStyles()

  const redirectToCreateProfile = () => {
    history.push('/pga-coach/profile/new')
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'coach-profile',
      formAction: 'clicked-create-coach-profile-button',
    })
  }

  const { data, loading, error, refetch } = useQuery(MY_COACH_PROFILE)
  const [toggleCoachStatus] = useMutation(ToggleCoachStatus, {
    onCompleted: refetch,
  })
  const [isQRcodeEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_QR_CODE])

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const coachProfile = get(data, 'currentUser.attributes.coachProfile')
  const facilities = get(data, 'currentUser.attributes.facilities')

  const customSlug = coachProfile?.coach?.customSlug // customSlug is only available for coaches who can promote their profile
  const hasActiveCoachProfile = coachProfile?.status === 'ACTIVE'
  const coachProfileUrl = `${PGA_COM_HOST}/pga-coach/${customSlug}`

  return (
    <>
      {coachProfile != null ? (
        <Grid container>
          <Grid
            container
            spacing={4}
            className={classes.coachProfileCardsContainer}
          >
            <Grid item xs={12} lg={4}>
              <Box mb={-2}>
                <CoachProfileCard
                  coachProfile={coachProfile}
                  toggleCoachStatus={toggleCoachStatus}
                />
              </Box>
            </Grid>
            <Hidden smUp>
              {isQRcodeEnabled && hasActiveCoachProfile && (
                <Grid item xs={12}>
                  <Paper variant="outlined" className={classes.qrCodeCard}>
                    <Grid
                      item
                      xs={12}
                      lg={8}
                      container
                      direction="column"
                      alignItems="center"
                      className={classes.containerQRCode}
                    >
                      <Typography
                        variant="h6"
                        style={{
                          paddingBottom: '10px',
                        }}
                      >
                        Your Coach Profile
                      </Typography>
                      <QRCode
                        data-testid="coach-profile-qr-code"
                        value={coachProfileUrl}
                        size={140} // in px
                        level="H" // error correction level (H = high) for more squareness and less likely to be misread
                        className={classes.qrCode}
                      />
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Hidden>
            <Grid
              item
              xs={12}
              lg={8}
              className={classes.analyticsCardContainer}
            >
              <CoachProfileAnalyticsCard
                coachProfile={coachProfile}
                facilities={facilities}
              />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <EmptyPage
          actionFn={redirectToCreateProfile}
          IconComp={() => <PersonOutlineOutlinedIcon fontSize="large" />}
          title="Coach Profile"
          caption="Your PGA Coach Profile will appear here once completed."
          buttonText="Create Coach Profile"
        />
      )}
    </>
  )
}

const CoachProfileIndexPage = () => {
  const { path } = useRouteMatch()

  const crumbs = [
    {
      label: 'Home',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
  ]

  return (
    <>
      <PageHeader title="Coach Profile" crumbs={crumbs} />
      <CoachProfileDisplay />
    </>
  )
}

export default CoachProfileIndexPage
